import { create } from 'zustand';
import type { MapOverlay } from '../types';

// Ephemeral working state for the plan-map-overlay corner-placement editor
// (Sept 2026 phase 2). Split out of uiStore because it's shared between two
// siblings that otherwise never talk to each other: PlanMapPlacementPanel.web
// (the form — opacity slider, Save/Publish/Reset) and
// usePlanMapPlacementLayer.web (the Leaflet drag gesture, mounted in
// Map.web.tsx) — both read `corners` to know the current placement, and
// either can write it (the Leaflet hook on every drag frame, the panel's
// Reset button). uiStore.placingOverlayId says WHICH overlay is being
// placed; this store holds WHERE it currently sits, uncommitted.
//
// Nothing here reaches Firestore until the panel's Save/Publish calls
// mapOverlaysApi.updateOverlay — that's the whole point of keeping it out of
// the overlay doc (and out of mapOverlaysStore) until then.
/** Whether dragging the image body moves the image (default) or is ignored
 *  so the same drag pans the map underneath. The three corner handles stay
 *  draggable in either mode — only the image body's own gesture changes. */
export type PlanMapPlacementMode = 'image' | 'map';

interface PlanMapPlacementState {
  corners: MapOverlay['corners'] | null;
  opacity: number;
  mode: PlanMapPlacementMode;
  /** Seeds working state when placement starts — either the doc's saved
   *  corners, or a fresh fit-to-view rectangle (the caller decides which).
   *  Always resets mode to 'image', the default for a fresh session. */
  initialize: (corners: MapOverlay['corners'], opacity: number) => void;
  setCorners: (corners: MapOverlay['corners']) => void;
  setOpacity: (opacity: number) => void;
  setMode: (mode: PlanMapPlacementMode) => void;
  /** Bumped by the panel's "Fit to view"; the Leaflet hook watches it and
   *  re-seeds `corners` from whatever the map is showing. A counter, not a
   *  boolean, so pressing it twice on the same view still fires twice — the
   *  panel has no map to compute the rectangle from, and the hook has no
   *  button, so the request has to cross between them. Same shape as the
   *  save/claim request gates elsewhere. */
  fitRequested: number;
  requestFit: () => void;
  /** Called when placement ends (Save/Publish/Cancel) so a later Escape or
   *  stray render never sees a previous overlay's corners. */
  clear: () => void;
}

export const usePlanMapPlacementStore = create<PlanMapPlacementState>((set) => ({
  corners: null,
  opacity: 0.6,
  mode: 'image',
  fitRequested: 0,
  initialize: (corners, opacity) => set({ corners, opacity, mode: 'image', fitRequested: 0 }),
  setCorners: (corners) => set({ corners }),
  setOpacity: (opacity) => set({ opacity }),
  setMode: (mode) => set({ mode }),
  requestFit: () => set((s) => ({ fitRequested: s.fitRequested + 1 })),
  clear: () => set({ corners: null, opacity: 0.6, mode: 'image', fitRequested: 0 }),
}));
